/**
 * Structural equality for values that came off the wire — "did the user change
 * this JSON document?".
 *
 * Serialization-based, which makes it ORDER-SENSITIVE for object keys: two
 * documents with the same entries in a different order compare unequal. That is
 * the right trade for its callers, which seed an editor with a stored document
 * and ask afterwards whether it still matches — the editor preserves the order
 * it was given, so the only way to reorder keys is to have rewritten the value.
 *
 * Answering "unequal" too eagerly costs a redundant no-op swap; answering
 * "equal" too eagerly would DROP a change the user made, so the bias is
 * deliberate.
 *
 * Extracted (BDA-6120) from `cube-lib`'s private `deepEqual`, which both cubes'
 * copy dialogs need to decide whether a replaceable entity was edited.
 */
export function bdStructuralEquals(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null) return a === b;
  if (typeof a !== typeof b) return false;
  if (typeof a !== 'object') return a === b;
  return JSON.stringify(a) === JSON.stringify(b);
}

export class BdEquals {
  /**
   * Check, if nullable [value] is equals to nullable [other], using default compare function or custom.
   * @param value
   * @param other
   * @param compareFn
   */
  public static equals<TValue>(
    value?: TValue,
    other?: TValue,
    compareFn: (a: TValue, b: TValue) => boolean = (a, b) => a === b,
  ): boolean {
    if (value === undefined && other === undefined) {
      return true;
    }

    if (value === null && other === null) {
      return true;
    }

    if (typeof value === 'boolean') {
      return value === other;
    }

    if (value != null && other != null) {
      return compareFn(value, other);
    } else {
      return false;
    }
  }
}
